// API client for the taco HTTP backend. Uses same-origin relative
// paths; the dev server (ui/src/index.ts) proxies /api/* to the Go
// server on :8080 so Bun's HMR can coexist with the real backend.

export type Trend = "up" | "down" | "neutral" | "unknown";

/** Severity level for a QualityFlag. Matches
 *  ranking.Severity in Go. "block" = the scanner rejects the row;
 *  the main pipeline annotates all severities without rejecting. */
export type QualitySeverity = "block" | "warn";

/** A quality concern attached to a ranking.Row by the shared
 *  ranking.EvaluateQualityFlags function. The UI renders a "!" pill
 *  colored by the highest severity present on the row, with the
 *  tooltip text assembled from the messages. */
export interface QualityFlag {
  code: string;
  severity: QualitySeverity;
  message: string;
}

/** NaN-safe Yahoo-derived fundamentals. Nulls mean "missing" per
 *  the same finiteOrNil convention used elsewhere on the wire —
 *  the UI renders a dash, never a zero. */
export interface Fundamentals {
  ticker?: string;
  market_cap: number | null;
  shares_outstanding: number | null;
  float_shares: number | null;
  short_percent_of_float: number | null;
  /** ISO 8601 timestamp when an earnings date is known; null
   *  otherwise. */
  next_earnings_date: string | null;
  fetched?: string | null;
}

export interface Row {
  ticker: string;
  current_price: number;
  support: number;
  resistance: number;
  hit_rate: number | null;
  /** Event-based bounce rate (Phase 2): share of resolved historical
   *  support-zone touches that reached the resistance zone before
   *  failing. Null = no resolved episodes ("insufficient data"). */
  bounce_rate: number | null;
  /** Winning level-cluster sizes (Phase 2/3a). 0 = the level came
   *  from the close-based fallback chain — synthetic, not traded;
   *  such rows carry the synthetic_level warn flag. */
  support_touches: number;
  resistance_touches: number;
  /** Last bar's volume over the trailing 20-bar average of the
   *  prior bars. 1.0 = normal volume. Display-only decision
   *  support (not scored). Null = insufficient volume data. */
  relative_volume: number | null;
  atr_pct: number;
  /** 20-day average close × volume in USD. Computed by
   *  analysis.AvgDollarVolume and surfaced on every ranking.Row so
   *  the main table can display liquidity alongside the scanner's
   *  gate. Null when the helper returned 0 (missing volume data or
   *  all-NaN window) — the wire shape's finiteOrNilPositive
   *  collapses zero to null so the UI renders a dash instead of a
   *  misleading "$0". */
  avg_dollar_volume: number | null;
  trend: Trend;
  relative_strength: number | null;
  expected_return_pct: number;
  shares: number;
  expected_pnl: number;
  current_position: number;
  stop_loss: number;
  risk_pct: number | null;
  reward_risk: number | null;
  trail_distance: number;
  benchmark: string;
  trigger: number;
  drop_pct: number;
  overall_score: number;
  structural_score: number;
  /** NASDAQ Trader "Security Name" (e.g. "Apple Inc. - Common Stock").
   *  Populated by the analyze pipeline and the scanner from the
   *  shared universe map; omitted when unavailable. */
  security_name?: string;
  /** GICS sector from Yahoo's quoteSummary assetProfile. Only filled
   *  on the /api/analyze path — the scanner loop does not fetch per-
   *  ticker company info to stay inside Yahoo's rate budget. */
  sector?: string;
  industry?: string;
  description?: string;
  /** Shared quality-flag annotations (low_rr, atr_extreme,
   *  parabolic_60d, thin_float, illiquid_vs_float, earnings_5d,
   *  synthetic_level).
   *  Populated by ranking.EvaluateQualityFlags; the main pipeline
   *  annotates all rows (never rejects), so a row can carry flags
   *  and still appear in the ranked table. */
  quality_flags?: QualityFlag[];
  /** Point-to-point return over the last 30 / 60 trading sessions.
   *  Null on short history. */
  trailing_return_30: number | null;
  trailing_return_60: number | null;
  /** Number of overnight gaps ≥3% in the last 60 sessions. Zero
   *  is a valid "no gaps" answer. */
  recent_gap_count: number;
  /** Ratio of current close to the highest/lowest close over the
   *  prior 30 sessions (excluding current bar). 1.0 = at the prior
   *  high; <1.0 = pulled back. Null on short history. */
  price_vs_high_30: number | null;
  price_vs_low_30: number | null;
  /** Yahoo quoteSummary fundamentals. Absent when no lookup was
   *  wired or the Yahoo call failed. */
  fundamentals?: Fundamentals;
}

export interface Swing {
  start_idx: number;
  end_idx: number;
  start_price: number;
  end_price: number;
  direction: "up" | "down";
  amplitude_pct: number;
  duration: number;
}

export interface AnalysisDetail {
  ticker: string;
  current_price: number;
  support: number;
  resistance: number;
  current_position: number;
  atr: number;
  atr_pct: number;
  hit_rate: number | null;
  bounce_rate: number | null;
  /** Winning level-cluster sizes; 0 = fallback (synthetic) level. */
  support_touches: number;
  resistance_touches: number;
  sma50: number | null;
  trend: Trend;
  relative_strength: number | null;
  benchmark: string;
  regime_band: [number, number];
  swings: Swing[];
  peak_indices: number[];
  trough_indices: number[];
  dates: string[];
  close: number[];
  // Per-bar series mirrored from internal/analysis/swing.go. NaN slots
  // are serialized as null by the Go marshaler (finiteSlice helper).
  atr_series: (number | null)[];
  // Omitted when no benchmark was resolved; otherwise aligned to `dates`
  // with leading nulls for positions before the benchmark's first date.
  benchmark_close?: (number | null)[];
}

export interface TickerStatus {
  Ticker: string;
  Benchmark: string;
  BenchReason: string;
  CacheHit: boolean;
  SkippedError: string;
}

export interface AnalyzeRequest {
  tickers?: string[];
  capital?: number;
  lookback?: string;
  interval?: string;
  benchmark?: string;
  /** Bypass the on-disk daily cache for this one request. Set by the
   *  UI refresh button — forces a fresh Yahoo fetch for every ticker. */
  no_cache?: boolean;
  /** Skip the server's tracker-baseline update for this request.
   *  Set to true on one-off scanner-row detail lookups so the main
   *  watchlist baseline (e.g. 20 tickers) isn't replaced with the
   *  single clicked ticker. */
  skip_tracker?: boolean;
}

/** Live intraday snapshot emitted by the tracker's 5-minute poll loop.
 *  Matches internal/tracker/signals.go Snapshot. State drives the live
 *  cell color; pct_from_support feeds the ΔSup column. */
export type TrackerState = "idle" | "armed" | "triggered" | "invalidated";

export interface TrackerSnapshot {
  ticker: string;
  live_price: number;
  prior_close: number;
  change_pct: number;
  pct_from_support: number;
  pct_from_entry: number;
  pct_from_stop: number;
  /** (live - support) / atr. null on the wire when ATR <= 0 (degenerate
   *  baseline); the server encodes the non-finite value as JSON null per
   *  the finiteOrNil convention, and the UI renders it as a dash. */
  support_atrs: number | null;
  high_of_day: number;
  low_of_day: number;
  bar_open: number;
  bar_high: number;
  bar_low: number;
  bar_close: number;
  prev_bar_close: number;
  state: TrackerState;
  /** Human-readable explanation of the State, computed server-side by
   *  explainState(). Examples: "setup — 4.2% above support (arms at
   *  0.5%)", "price broke stop", "not a setup". The UI renders this
   *  verbatim so Idle is never a dead word. */
  reason: string;
  /** Wall-clock timestamp of the last 1-min bar Yahoo returned ("price
   *  as of" time). May be several seconds older than updated_at — Yahoo
   *  stamps bars on the minute boundary and we poll between them. */
  bar_time: string;
  /** Server poll time: when the tracker last asked Yahoo for this
   *  ticker. Differs from bar_time by up to one poll cycle. */
  updated_at: string;
}

// rows_by_mode is computed server-side from a single fetch+analyze run;
// the UI flips between the three slices locally without re-hitting the
// API. Each slice is already filtered and sorted for its mode.
export interface AnalyzeResponse {
  rows_by_mode: Record<"setup" | "watch" | "all", Row[]>;
  analyses: Record<string, AnalysisDetail>;
  statuses: TickerStatus[];
}

export interface Settings {
  DefaultWatchlist: string[];
  DefaultCapital: number;
  DefaultLookback: string;
  DefaultInterval: string;
  OutputDir: string;
  DefaultBenchmark: string;
  BenchmarkOverrides: Record<string, string>;
  CacheTTLSeconds: number;
  PinToClose: string;
  TrackPollSeconds: number;

  ScannerEnabled: boolean;
  ScannerTickDelayMs: number;
  ScannerUniverseRefreshHours: number;
  ScannerMinHitScore: number;
  ScannerMinHitRate: number;
  ScannerMinAtrPct: number;
  ScannerMinPrice: number;
  ScannerMinDollarVolume: number;
  ScannerIncludeETFs: boolean;
  ScannerUniverseFile: string;
  ScannerFinancialStatusDeny: string[];
  ScannerRegimeGate: boolean;
  ScannerRegimeBenchmark: string;
  ScannerRegimeSMA: number;

  /** Shared quality gates. Consumed by both scanner.PassesHitGate
   *  and pipeline.Run via ranking.EvaluateQualityFlags — one config,
   *  two consumer policies. */
  QualityMinRR: number;
  QualityMaxAtrPct: number;
  QualityMaxTrailing60DReturn: number;
  QualityMinFloatMcap: number;
  QualityMaxDollarVolPctFloat: number;
  QualityEarningsBufferDays: number;

  ServerPort: number;
}

/** One row in GET /api/scanner/results. Mirrors the Go scannerItem
 *  shape: flattened ranking.Row fields with NaN → null via the
 *  finiteOrNil helper, plus the scanner-specific LastScan and
 *  PassedSetup metadata. */
export interface ScannerItem {
  ticker: string;
  last_scan: string;
  scan_count: number;
  passed_setup: boolean;
  consecutive_errs: number;
  last_error?: string;
  overall_score: number;
  structural_score: number;
  current_price: number | null;
  support: number | null;
  resistance: number | null;
  hit_rate: number | null;
  bounce_rate: number | null;
  support_touches: number;
  resistance_touches: number;
  relative_volume: number | null;
  atr_pct: number | null;
  /** 20-day average close × volume. Stamped by the scanner's
   *  scanOne helper so PassesHitGate can reject ultra-thin tickers
   *  below scanner_min_dollar_volume. Null when the scanner hasn't
   *  computed it yet (cached row from a pre-liquidity binary).
   *  Expressed in USD, not a fraction — display as $1.2M, etc. */
  avg_dollar_volume: number | null;
  trend: Trend | "";
  relative_strength: number | null;
  expected_return_pct: number | null;
  current_position: number | null;
  stop_loss: number | null;
  risk_pct: number | null;
  reward_risk: number | null;
  benchmark: string;
  trigger: number | null;
  drop_pct: number | null;
  security_name?: string;
  sector?: string;
  industry?: string;
  description?: string;
  /** Quality-flag annotations, same shape and semantics as
   *  Row.quality_flags. Scanner results are re-evaluated at read
   *  time against the current live quality gates, so tuning
   *  quality_min_rr (etc.) via PUT /api/settings updates the flag
   *  list on the very next GET without waiting for the scan loop. */
  quality_flags?: QualityFlag[];
  trailing_return_30: number | null;
  trailing_return_60: number | null;
  recent_gap_count: number;
  price_vs_high_30: number | null;
  price_vs_low_30: number | null;
  fundamentals?: Fundamentals;
}

export interface ScannerResults {
  total: number;
  scanned: number;
  items: ScannerItem[];
  generated_at: string;
  min_score: number;
}

export interface ScannerStatus {
  running: boolean;
  universe_size: number;
  scanned: number;
  passed_hits: number;
  errored: number;
  last_universe_refresh: string;
  queue_depth: number;
  circuit_open: boolean;
  circuit_cooldown_sec: number;
  last_error?: string;
  min_hit_score: number;
  regime_gate: boolean;
  regime_favorable: boolean;
  regime_benchmark?: string;
}

export type ScannerEvent =
  | {
      type: "progress";
      progress: {
        scanned: number;
        total: number;
        queue_depth: number;
        current?: string;
        last_tick_at: string;
        circuit_open: boolean;
      };
    }
  | { type: "hit"; hit: ScannerItem }
  | { type: "status"; status: ScannerStatus }
  | { type: "error"; message: string };

export interface ScannerControlResponse {
  action: string;
  status: ScannerStatus;
  count?: number;
  message?: string;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const resp = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body
      ? { "content-type": "application/json", "X-Requested-With": "XMLHttpRequest" }
      : { "X-Requested-With": "XMLHttpRequest" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (resp.status === 401) {
    // In multi-tenant mode the server returns 401 JSON when the
    // session cookie is missing / expired / tampered. Redirect to the
    // server-side /auth/login endpoint, which 302s to the central
    // auth service with the right ?app=&return_to= params.
    const current = window.location.pathname + window.location.search;
    window.location.href = `/auth/login?to=${encodeURIComponent(current)}`;
    throw new Error("unauthenticated");
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`${method} ${path} → ${resp.status}: ${text}`);
  }
  return (await resp.json()) as T;
}

/** Identity of the current session. `authenticated=false` means the
 *  server runs in legacy single-tenant mode (no auth configured) so
 *  the UI can hide the header's auth bits. */
export interface Me {
  user_id?: string;
  org_id?: string;
  /** Human-readable org name from the auth launch token's `org_name`.
   *  Optional — old session cookies minted before the field landed
   *  simply won't include it. */
  org_name?: string;
  email?: string;
  /** Org role (owner/admin/member). Kept for display; do not gate
   *  per-app features on it — use app_role instead. */
  role?: string;
  /** Per-install, app-defined role resolved by the auth service.
   *  Surfaced in the UserMenu chip pill; gate per-app features on
   *  this when the first such gate lands. */
  app_role?: string;
  platform_admin: boolean;
  authenticated: boolean;
  /** True when the server runs in multi-tenant mode (an auth service is
   *  configured), regardless of whether this request is authenticated.
   *  Lets the SPA distinguish a logged-out MT user (auth_enabled &&
   *  !authenticated → show the landing page) from legacy no-auth mode
   *  (auth_enabled false → go straight to the dashboard). Optional so
   *  pre-existing legacy responses that omit it stay type-compatible. */
  auth_enabled?: boolean;
}

export function getMe(): Promise<Me> {
  return request<Me>("GET", "/api/me");
}

/** /auth/logout response. post_logout_url is the auth service's
 *  redirect-style logout endpoint (or "/" in legacy mode without an
 *  auth service). The SPA must navigate the browser to it after
 *  receiving this so auth's refresh cookie also gets cleared —
 *  otherwise the next visit silently re-mints a launch token and the
 *  user appears logged in again ("broken-button" UX). */
export interface LogoutResponse {
  ok: boolean;
  post_logout_url: string;
}

export function logout(): Promise<LogoutResponse> {
  return request<LogoutResponse>("POST", "/auth/logout");
}

/** Build tag, goreleaser-stamped. Empty string when the server was
 *  built via `go run` / `go build` without the ldflag — the footer
 *  falls back to a "dev" label in that case. */
export interface VersionInfo {
  version: string;
}

export function getVersion(): Promise<VersionInfo> {
  return request<VersionInfo>("GET", "/api/version");
}

// --- Admin-only read surfaces for /admin page. Each returns 403
// when the caller isn't PlatformAdmin (server-side check); the UI
// hides the admin button from non-admins so this should only fire
// for admins in practice.

/** One row of GET /api/admin/tenants — a tracker's per-org footprint. */
export interface AdminOrgInspect {
  org_id: string;
  baselines: number;
  subscribers: number;
}

export interface AdminTenantsResponse {
  orgs: AdminOrgInspect[];
}

export function getAdminTenants(): Promise<AdminTenantsResponse> {
  return request<AdminTenantsResponse>("GET", "/api/admin/tenants");
}

/** One snapshot-per-ticker from GET /api/admin/tracker/inspect. Latest
 *  may be absent when the poll loop hasn't landed yet for a freshly-
 *  set baseline. */
export interface AdminTickerInspect {
  ticker: string;
  orgs: string[];
  latest?: {
    ticker: string;
    state: string;
    reason: string;
    live_price: number;
    prior_close: number;
    distance_to_support_pct: number;
    distance_to_support_atr: number;
    // Other Snapshot fields are present but the admin table renders
    // only the headline metrics; TS keeps typing loose with the
    // extension.
    [key: string]: unknown;
  };
}

export interface AdminTrackerInspectResponse {
  tickers: AdminTickerInspect[];
  orgs: AdminOrgInspect[];
}

export function getAdminTrackerInspect(): Promise<AdminTrackerInspectResponse> {
  return request<AdminTrackerInspectResponse>("GET", "/api/admin/tracker/inspect");
}

/** Multi-tenant settings envelope. The server returns this shape when
 *  Registry + Auth are wired; single-tenant deployments still return
 *  the flat `Settings` struct. Distinguish via the `multi_tenant` flag
 *  at the top level of the response. */
export interface OrgSettings {
  default_watchlist?: string[];
  default_capital?: number;
  default_lookback?: string;
  default_interval?: string;
  default_benchmark?: string;
  benchmark_overrides?: Record<string, string>;
  pin_to_close?: string;
}

export interface ProcessSettings {
  cache_ttl_seconds?: number;
  track_poll_seconds?: number;
  scanner_enabled?: boolean;
  scanner_tick_delay_ms?: number;
  scanner_universe_refresh_hours?: number;
  scanner_min_hit_score?: number;
  scanner_min_hit_rate?: number;
  scanner_min_atr_pct?: number;
  scanner_min_price?: number;
  scanner_min_dollar_volume?: number;
  scanner_include_etfs?: boolean;
  scanner_universe_file?: string;
  scanner_financial_status_deny?: string[];
  scanner_regime_gate?: boolean;
  scanner_regime_benchmark?: string;
  scanner_regime_sma?: number;
  quality_min_rr?: number;
  quality_max_atr_pct?: number;
  quality_max_trailing_60d_return?: number;
  quality_min_float_mcap?: number;
  quality_max_dollar_vol_pct_float?: number;
  quality_earnings_buffer_days?: number;
}

export interface SettingsEnvelope {
  org: OrgSettings;
  process: ProcessSettings;
  is_admin: boolean;
  multi_tenant: true;
}

/** Union return: single-tenant (`Settings`) or multi-tenant (`SettingsEnvelope`).
 *  Callers distinguish via `"multi_tenant" in response`. */
export type SettingsResponse = Settings | SettingsEnvelope;

export function getSettingsEnvelope(): Promise<SettingsResponse> {
  return request<SettingsResponse>("GET", "/api/settings");
}

export function putSettingsEnvelope(
  s: Partial<Record<string, unknown>>,
): Promise<SettingsResponse> {
  return request<SettingsResponse>("PUT", "/api/settings", s);
}

export function analyze(req: AnalyzeRequest): Promise<AnalyzeResponse> {
  return request<AnalyzeResponse>("POST", "/api/analyze", req);
}

/** Flatten the multi-tenant settings envelope into the legacy flat
 *  `Settings` shape so callers written for the original single-tenant
 *  server (App.tsx, SettingsSheet.tsx) don't need to branch on server
 *  mode. Shallow merges `org` + `process` into the Pascal-cased flat
 *  struct. When the server runs in legacy mode, the response is
 *  already flat and this function is a no-op.
 *
 *  The `isAdmin` signal from the envelope is attached as a hidden
 *  field on the returned object — not part of the Settings interface
 *  so the legacy server's flat response stays schema-compatible —
 *  consumers that need it read `(s as any)._is_admin`. */
function flattenSettingsResponse(resp: SettingsResponse): Settings & { _is_admin?: boolean } {
  if (!("multi_tenant" in resp)) {
    return resp;
  }
  const org = resp.org ?? {};
  const proc = resp.process ?? {};
  return {
    DefaultWatchlist: org.default_watchlist ?? [],
    DefaultCapital: org.default_capital ?? 0,
    DefaultLookback: org.default_lookback ?? "180d",
    DefaultInterval: org.default_interval ?? "1d",
    // OutputDir is server-side process config in MT mode; the UI
    // never edits it directly. Surface as empty string so the legacy
    // form renders cleanly.
    OutputDir: "",
    DefaultBenchmark: org.default_benchmark ?? "IWM",
    BenchmarkOverrides: org.benchmark_overrides ?? {},
    CacheTTLSeconds: proc.cache_ttl_seconds ?? 0,
    PinToClose: org.pin_to_close ?? "auto",
    TrackPollSeconds: proc.track_poll_seconds ?? 300,
    ScannerEnabled: proc.scanner_enabled ?? false,
    ScannerTickDelayMs: proc.scanner_tick_delay_ms ?? 2000,
    ScannerUniverseRefreshHours: proc.scanner_universe_refresh_hours ?? 168,
    ScannerMinHitScore: proc.scanner_min_hit_score ?? 4,
    ScannerMinHitRate: proc.scanner_min_hit_rate ?? 0.25,
    ScannerMinAtrPct: proc.scanner_min_atr_pct ?? 0.005,
    ScannerMinPrice: proc.scanner_min_price ?? 5,
    ScannerMinDollarVolume: proc.scanner_min_dollar_volume ?? 1_000_000,
    ScannerIncludeETFs: proc.scanner_include_etfs ?? false,
    ScannerUniverseFile: proc.scanner_universe_file ?? "",
    ScannerFinancialStatusDeny: proc.scanner_financial_status_deny ?? [],
    ScannerRegimeGate: proc.scanner_regime_gate ?? true,
    ScannerRegimeBenchmark: proc.scanner_regime_benchmark ?? "IWM",
    ScannerRegimeSMA: proc.scanner_regime_sma ?? 200,
    QualityMinRR: proc.quality_min_rr ?? 1.5,
    QualityMaxAtrPct: proc.quality_max_atr_pct ?? 0.1,
    QualityMaxTrailing60DReturn: proc.quality_max_trailing_60d_return ?? 1.0,
    QualityMinFloatMcap: proc.quality_min_float_mcap ?? 30_000_000,
    QualityMaxDollarVolPctFloat: proc.quality_max_dollar_vol_pct_float ?? 0.05,
    QualityEarningsBufferDays: proc.quality_earnings_buffer_days ?? 5,
    ServerPort: 0,
    _is_admin: resp.is_admin,
  };
}

export async function getSettings(): Promise<Settings & { _is_admin?: boolean }> {
  const resp = await request<SettingsResponse>("GET", "/api/settings");
  return flattenSettingsResponse(resp);
}

export async function putSettings(
  s: Partial<Record<string, unknown>>,
): Promise<Settings & { _is_admin?: boolean }> {
  const resp = await request<SettingsResponse>("PUT", "/api/settings", s);
  return flattenSettingsResponse(resp);
}

export function scannerResults(params: {
  minScore?: number;
  limit?: number;
  offset?: number;
  sort?: "score" | "recent";
  passedOnly?: boolean;
}): Promise<ScannerResults> {
  const qs = new URLSearchParams();
  if (params.minScore !== undefined) qs.set("min_score", String(params.minScore));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  if (params.sort) qs.set("sort", params.sort);
  if (params.passedOnly) qs.set("passed_only", "true");
  const q = qs.toString();
  return request<ScannerResults>(
    "GET",
    q ? `/api/scanner/results?${q}` : "/api/scanner/results",
  );
}

export function scannerStatus(): Promise<ScannerStatus> {
  return request<ScannerStatus>("GET", "/api/scanner/status");
}

export function scannerControl(
  action: "start" | "stop" | "refresh" | "reset" | "reapply",
): Promise<ScannerControlResponse> {
  return request<ScannerControlResponse>("POST", "/api/scanner/control", { action });
}

/** Aggregate health snapshot the SystemStatusBar polls every few
 *  seconds. Both nested slots are nullable to mirror the Go handler's
 *  tolerance of a server that wired only one of the background loops
 *  (tests, CLI-only mode). */
export interface SystemStatus {
  generated_at: string;
  tracker?: {
    baselines: number;
    subscribers: number;
    interval_sec: number;
    market_open: boolean;
    last_poll_at: string;
    last_error?: string;
  };
  scanner?: {
    running: boolean;
    circuit_open: boolean;
    circuit_cooldown_sec: number;
    universe_size: number;
    scanned: number;
    queue_depth: number;
    errored: number;
    last_error?: string;
  };
}

export function systemStatus(): Promise<SystemStatus> {
  return request<SystemStatus>("GET", "/api/system/status");
}
